import {
  createStagedEvent,
  defaultStagedThreshold,
  EventCancellation
} from '../../../webmodule-common/other/general/staged-event';
import { DataEntryOwner } from '../../../webmodule-common/other/ui/DataEntryOwner';
import { DataEntryViewController, ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { disposeOf } from '../../../webmodule-common/other/dispose';
import { EAbort } from '../../../webmodule-common/other/abort';
import { getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import { goStaticURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { html } from 'lit';
import { isEmptyOrSpace, validId } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangAppResourceBase } from '../../../localization/scripts/dealer-franchisee/pages/app-resource-base-lang';
import { lockUIandExecute } from '../../../webmodule-common/other/ui-lock';
import { notificationSignal } from '../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { NullPromise } from '../../../webmodule-common/interop/types/misc';
import { PageBase } from './helpers/page-base';
import { property, state } from 'lit/decorators.js';
import { ResourceLocker } from '../common/resource-lock';
import { showDevelopmentError } from '../../../webmodule-common/other/development-error';

export class AppResourcePage extends PageBase implements DataEntryOwner {
  internalId: string = getInternalId();
  private _retainLock = false;
  @state()
  protected modalEditor: ModalViewBase | null = null;
  protected lock?: ResourceLocker;
  @property({ type: Number })
  protected renderCount = 0;
  @property({ type: Boolean })
  protected resourceChangeEventTriggered = false;

  private _eventCancel: EventCancellation = new EventCancellation();
  private _resourceChangeEvent = createStagedEvent({
    testInterval: 500,
    cancelToken: this._eventCancel,
    threshold: defaultStagedThreshold,
    eventFinally: () => notificationSignal(false),
    event: async () => {
      this._retainLock = this.lock !== undefined && !this.lock.isLockOwner;

      await this.resourceRefreshing();

      await this.acquireResource(this.resourceId);
      //  await this.allowEnter();

      await this.setActiveTabByHash();

      this.resourceChangeEventTriggered = false;
    },
    eventTriggered: () => {
      this.resourceChangeEventTriggered = true;
      notificationSignal(true);
    }
  });
  @state()
  otherUpdatesOccurring = 0;
  private resourceChangeEvent = async (e: Event) => {
    e.stopImmediatePropagation();
    this._resourceChangeEvent();
  };
  private otherUpdatesOccurringEvent = async (e: Event) => {
    const ev = e as CustomEvent<boolean>;
    e.stopImmediatePropagation();
    if (ev.detail) this.otherUpdatesOccurring++;
    else this.otherUpdatesOccurring--;
  };

  protected async resourceRefreshing() {
    //throw new Error('Method not implemented.');
  }
  constructor() {
    super();
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('resource-changed', this.resourceChangeEvent);
    this.addEventListener('related-changed', this.otherUpdatesOccurringEvent);
  }

  disconnectedCallback() {
    this._eventCancel.cancel();
    super.disconnectedCallback();
    this.removeEventListener('resource-changed', this.resourceChangeEvent);
    this.removeEventListener('related-changed', this.otherUpdatesOccurringEvent);
  }

  protected get resourceId(): string {
    const l = this.location;
    const p = l.params as any;
    return p.id ?? '';
  }

  protected get view(): DataEntryViewController | null {
    //return this.modalEditor?.view ?? null;
    return this.modalEditor?.viewController ?? null;
  }

  private get elementId(): string {
    return `resourceeditor-${this.internalId}`;
  }

  async createModalEditor(_id: string): NullPromise<ModalViewBase> {
    throw new Error('Method not implemented.');
  }

  async allowEnter(): Promise<boolean> {
    if (!validId(this.resourceId)) return false;

    return (
      (await lockUIandExecute(async () => {
        return await this.acquireResource(this.resourceId);
      })) ?? false
    );
  }

  async tryClose(): Promise<boolean> {
    const result = (await this.modalEditor?.tryClose()) ?? true;
    if (result) {
      const url = '/';
      goStaticURL(url);
    }
    return result;
  }

  async forceClose(): Promise<boolean> {
    const url = '/';
    goStaticURL(url);
    return true;
  }

  protected forcedReadonly = false;
  protected forcedReadonlyMsg = '';

  /**
   * Override in subclasses to indicate if the resource itself is in a readonly state.
   * This is separate from lock-based readonly (e.g., another user has the lock).
   */
  protected isResourceReadonly(): boolean {
    return false;
  }

  render() {
    if (!this.loggedIn) return html``;
    const nameTemplate = isEmptyOrSpace(this.lock?.lockOwnerEmail)
      ? this.lock?.lockOwnerName
      : html`<a href="mailto:${this.lock?.lockOwnerEmail}">${this.lock?.lockOwnerName}</a>`;
    const isUnknownOwner = isEmptyOrSpace(this.lock?.lockOwnerName) && !this.lock?.isLockOwner;
    const resourceReadonly = this.isResourceReadonly();
    const lockMsg = this.forcedReadonly
      ? html`${LangAppResourceBase.readonly()} ${this.forcedReadonlyMsg}`
      : this.lock?.isLockOwner && !resourceReadonly
        ? LangAppResourceBase.editingMode()
        : isUnknownOwner
          ? html`${LangAppResourceBase.readonly()}`
          : resourceReadonly
            ? html`${LangAppResourceBase.readonly()}`
            : html`${LangAppResourceBase.readonlyCurrentlyInUseBy()}${nameTemplate}`;

    const lockMsgClass =
      this.lock?.isLockOwner && !this.forcedReadonly && !resourceReadonly
        ? LangAppResourceBase.resourceLockEditing()
        : LangAppResourceBase.resourceLockLocked();
    const lockedUserTemplate = html`<div class="badge resource-lock-owner ${lockMsgClass}">
      <i class="fa-solid fa-user-lock"></i>
      <span class="ms-1">${lockMsg}</span>
    </div>`;
    return html`
      <div id=${this.elementId} class="page-content">
        <div class="page-container">
          <div class="header">
            <h2 class="title">${this.view?.getTitle()}${lockedUserTemplate}</h2>
          </div>
          ${this.view?.ui}
        </div>
      </div>
    `;
  }

  async userReset() {
    await this.dispose(); //override
  }

  async dispose(): Promise<void> {
    try {
      await super.dispose();
      await disposeOf(this.view);
      this.modalEditor = null;
    } catch {
      //
    }
    try {
      if (this._retainLock) return;
      await this.lock?.release();
      this.lock = undefined;
    } catch {
      //
    }
  }

  protected createLock(_id: string): ResourceLocker | undefined {
    return undefined;
  }

  protected async canLeavePage(): Promise<boolean> {
    return this.modalEditor?.canClose() ?? true;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected async afterUserConnected() {
    if (!this.lock) if (!(await this.allowEnter())) await this.forceClose();
  }

  private async acquireResource(id: string): Promise<boolean> {
    if (!this._retainLock || !this.lock) {
      this.lock = this.createLock(id);
    } else {
      console.log('holding existing lock ');
    }
    const keepCurrentLock = this._retainLock;
    this._retainLock = false;

    if (!this.lock) return false;

    if (keepCurrentLock || (await this.lock.lock())) {
      try {
        const newEditor = await this.createModalEditor(id);
        await this.setActiveTabByHash(newEditor?.viewController);
        this.modalEditor = newEditor;
      } catch (error) {
        await this.lock.release();
        if (error instanceof EAbort) {
          console.log('Operation aborted');
          return false;
        } else if (error instanceof Error) await showDevelopmentError(error);
        return false;
      }
      if (this.view) {
        this.view.owner = this;
        return true;
      } else await this.lock.release();
    }
    return false;
  }

  protected async awaken() {
    await this.setActiveTabByHash();
    await super.awaken();
  }

  private async setActiveTabByHash(aView?: DataEntryViewController) {
    await (aView ?? this.view)?.setActiveTabByHash();
  }
}
